import * as React from 'react'

import { Dict } from '@clara/utils'
import { useAsyncFn } from '@clara/hooks'

import { DropdownOption, ReimbursementLabel } from '@/types/api'
import { getLabels } from '@/features/manage-reimbursement/api/metadata'

interface Label {
  labelUUID: string
  labelName: string
  canDelete?: boolean
}

type LoadingState = 'loading' | 'error' | 'success' | 'idle'

export function useLabelsOptions(reimbursementLabels?: ReimbursementLabel[]) {
  const [labelsData, init] = useAsyncFn(async () => {
    const response = await getLabels()
    return response
  })

  React.useEffect(() => {
    if (labelsData.value) return
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const disorderedLabels = (labelsData.value ?? []).map((label: Label) => ({
    value: label.labelUUID,
    label: label.labelName,
    canDelete:
      (reimbursementLabels ?? []).filter(
        (r) => r.labelUUID === label.labelUUID,
      )[0]?.canDelete ?? true,
  }))

  const labels = [...disorderedLabels].sort((a, b) => {
    if (!a.canDelete && b.canDelete) return -1
    if (a.canDelete && !b.canDelete) return 1
    return 0
  })

  const labelsDict = labels.reduce((acc: Dict<DropdownOption>, label) => {
    acc[label.value] = label
    return acc
  }, {})

  const loadingState: LoadingState = labelsData.loading
    ? 'loading'
    : labelsData.error
      ? 'error'
      : labelsData.value
        ? 'success'
        : 'idle'

  return {
    labels,
    labelsDict,
    loadingState,
    isLoading: loadingState === 'loading',
  }
}

export type UseLabelsOptionsReturn = ReturnType<typeof useLabelsOptions>
